<template>
  <sider-menu-layout :override-menus="menus">
    <template #sidebar-extra="{ collapsed }">
      <div
        v-if="currentProject && residentStore.allSysProject.length"
        class="project-selector-wrapper"
        :class="{ 'project-selector-wrapper-collapsed': collapsed }"
      >
        <project-switch
          :collapsed="collapsed"
          :current-project="currentProject"
          :projects="residentStore.allSysProject"
        />
      </div>
    </template>
    <template #content>
      <template v-if="currentProject">
        <router-view v-slot="{ Component }">
          <keep-alive :max="10">
            <content-fade-view v-if="route.meta.keepAlive" :key="route.name" :page="Component" />
          </keep-alive>
          <content-fade-view v-if="!route.meta.keepAlive" :key="route.name" :page="Component" />
        </router-view>
      </template>
      <!-- 保密项目：非成员（或项目不存在）解析不到 currentProject 时，给明确无权提示而非白屏。安全边界在后端 403。 -->
      <a-result
        v-else-if="!loading"
        status="403"
        title="无权访问该项目"
        sub-title="您不是该项目成员，或项目不存在。如需访问，请联系项目负责人将您加入项目。"
      >
        <template #extra>
          <a-button type="primary" @click="router.push('/workplace')">返回工作台</a-button>
        </template>
      </a-result>
    </template>
  </sider-menu-layout>
</template>
<script setup lang="ts">
import ContentFadeView from '@/components/layout/content-fade-view/index.vue';
import SiderMenuLayout from '@/components/layout/sider-menu-layout/index.vue';
import type { SiteMenuItem } from '@/router/types';
import { useAppStore, useResidentStore, useUserStore } from '@/store';
import { SysProjectType } from '@/types/common';
import { PermissionPageKey } from '@/types/permission';
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ProvideSiteProjectContext } from '../../utils/context';
import ProjectSwitch from './project-switch.vue';

const route = useRoute();
const router = useRouter();
const appStore = useAppStore();
const residentStore = useResidentStore();
const userStore = useUserStore();

const loading = ref(false);

const currentProject = computed(() => {
  const current = residentStore.allSysProject.find((e) => e.pkey === route.params.pkey);
  return current;
});

const menus = computed(() => {
  if (!currentProject.value) {
    return [];
  }
  if (currentProject.value?.type !== SysProjectType.Software) {
    return appStore.currentSiteMenus.map((e) => ({
      ...e,
      expandIcon: undefined as any,
    }));
  }
  return mapSoftwareMenus(appStore.currentSiteMenus);
});

const mapSoftwareMenus = (menus: SiteMenuItem[]): SiteMenuItem[] => {
  if (!menus) {
    return [];
  }
  return menus
    .filter((e) => !e.hideInSoftwareProject)
    .map((e) => {
      const filteredChildren = e.children ? mapSoftwareMenus(e.children) : undefined;
      return {
        ...e,
        expandIcon: undefined as any,
        children: filteredChildren && filteredChildren.length > 0 ? filteredChildren : undefined,
      };
    });
};

ProvideSiteProjectContext({ currentProject });

onMounted(() => {
  loading.value = true;
  residentStore.getAllSysProject().then((res) => {
    if (!route.params.pkey) {
      // 无可访问项目（如非成员/全是保密项目）时不跳转，避免 res[0] 取空崩溃，落到无权兜底
      if (!res || res.length === 0) {
        loading.value = false;
        return;
      }
      const firstAvailablePage = userStore.pagePermissions.includes(PermissionPageKey.ProjectDashboard)
        ? PermissionPageKey.ProjectDashboard
        : (appStore.currentSiteMenus[0]?.key as PermissionPageKey) || PermissionPageKey.ProjectDashboard;

      router.push({
        name: firstAvailablePage,
        params: { pkey: res[0].pkey },
      });
    }
    loading.value = false;
  });
});
</script>
<style scoped>
.project-selector-wrapper {
  padding: 4px 12px 8px;
  border-bottom: 1px solid #f2f3f5;
  margin-bottom: 4px;
}

.project-selector-wrapper-collapsed {
  padding: 4px 0 8px;
  display: flex;
  justify-content: center;
}

.project-selector-wrapper :deep(.project-switch-btn) {
  background: #f7f8fa;
  color: #1d2129;
  border: none;
  border-radius: 7px;
  padding: 8px 10px;
  font-size: 13px;
  margin: 0;
}

.project-selector-wrapper :deep(.project-switch-btn:hover) {
  background: #e8f3ff;
}

.project-selector-wrapper-collapsed :deep(.project-switch-btn) {
  width: 40px;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}
</style>
